import { GridColDef, GridEventListener, gridFilteredDescendantCountLookupSelector, GridGroupingColDefOverride, GridRenderCellParams, GridRowHeightParams, GridValidRowModel, isAutogeneratedRow, useGridApiContext, useGridApiRef, useGridSelector } from "@mui/x-data-grid-premium";
import { isWithin7Days } from "../data/helper";
import Stack from "@mui/material/Stack";
import { useCallback } from "react";
import DataGrid from "components/DataGrid/DataGrid";
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import COLORS from 'lib/colors'
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import clsx from "clsx";
import { isAutogeneratedRowNode } from "@mui/x-data-grid/hooks/features/rows/gridRowsUtils";
import { useRegisterGridForExport } from "../ExportRegistryProvider";


function TreeDataGroupingCell<R extends TreeDataGridRow>({ rowNode, row }: GridRenderCellParams<R>) {
  const apiRef = useGridApiContext()
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector,
  )
  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

  if(isAutogeneratedRowNode(rowNode)) {
    return null
  }

  return (
    <Stack direction='row-reverse' sx={{ height: '100%', alignItems: 'center', gap: 2, mr: '4px' }}>
      {filteredDescendantCount > 0 && rowNode.type === 'group' &&
        <>
          {rowNode.childrenExpanded ?
            <ExpandLessIcon sx={{ }}/>
          :
            <ExpandMoreIcon />
          }
        </>
      }
      {/* Reminder, autoHeight makes this bug out (and disappear) probably want to add it in it's own column */}
      {(isWithin7Days(row.updatedAt) || isWithin7Days(row.createdAt)) &&
        <>
          {rowNode.depth === 0 ?
            <Tooltip title={`Updated ${moment(row.updatedAt || row.createdAt).format('MM/DD/YY')}`}>
              <InfoOutlinedIcon sx={{ color: COLORS.coastalWaters }} />
            </Tooltip>
          :
            <Chip
              color="default"
              label='New'
              size="small"
              variant="filled"
            />
          }
        </>
      }
    </Stack>
  );
}

export interface TreeDataGridRow extends GridValidRowModel {
  id: string
  path: string[]
}

export type TreeDataGridProps<R extends TreeDataGridRow> = {
  rows: R[]
  columns: GridColDef<R>[]
  enableRowSpanning?: boolean
  exportRegistration?: {
    id: string
    sheetName: string
    order?: number
    rowSpanFields?: string[]
    rowSpanGroupByField?: string
  }
}

export default function TreeDataGrid<R extends TreeDataGridRow>({
  rows,
  columns,
  enableRowSpanning = false,
  exportRegistration,
}: TreeDataGridProps<R>) {
  const groupingColumnDef: GridGroupingColDefOverride<R> = {
    headerName: '',
    renderCell: (params: GridRenderCellParams<R>) => <TreeDataGroupingCell {...params} />,
  }

  const getRowClassName = useCallback(({ row }) => {
    return clsx({
      'child-row': !isAutogeneratedRow(row) && row.path.length > 1,
      'top-level-row': !isAutogeneratedRow(row) && row.path.length === 1,
    })
  }, [])

  const apiRef = useGridApiRef();

  useRegisterGridForExport({
    enabled: !!exportRegistration,
    id: exportRegistration?.id,
    sheetName: exportRegistration?.sheetName,
    order: exportRegistration?.order,
    rowSpanFields: exportRegistration?.rowSpanFields,
    rowSpanGroupByField: exportRegistration?.rowSpanGroupByField,
    apiRef,
  });

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ id }) => {
      const rowNode = apiRef?.current?.getRowNode(id);
      if (rowNode && !isAutogeneratedRowNode(rowNode) && rowNode.type === 'group') {
        apiRef?.current?.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      }
    },
    [apiRef],
  )

  const getRowHeight = useCallback((params: GridRowHeightParams) => (isAutogeneratedRow(params.model) ? 1 : params.model.path?.length > 1 ? 30 : 48), [])
  const getTreeDataPath = useCallback((row: R) => row.path, [])

  return (
    <DataGrid
      autoHeight
      disableColumnMenu
      getRowHeight={getRowHeight}
      apiRef={apiRef}
      unstable_rowSpanning={enableRowSpanning}
      treeData
      rows={rows}
      columns={columns}
      getTreeDataPath={getTreeDataPath}
      getRowClassName={getRowClassName}
      groupingColDef={groupingColumnDef}
      onRowClick={onRowClick}
      defaultGroupingExpansionDepth={-1}
      sx={{
        '& .MuiDataGrid-footerContainer': {
          display: 'none',
        },
        '.code-text': {
          fontFamily: 'monospace',
        },
        '.bold': {
          fontWeight: 600,
        },
        '.top-level-row, .top-level-row:hover': {
          backgroundColor: COLORS.veryLightGray,
        },
        '.child-row': {
          '--rowBorderColor': 'transparent',
        },
        '.child-row .MuiDataGrid-cell': {
          borderTop: 'none',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'inherit',
        },
        '& .MuiDataGrid-row.bottom-border': {
          borderBottom: `1px solid ${COLORS.mediumGray}`
        },
      }}
    />
  )
}
